<template>
  <ion-page>
    <ion-header class="page-header liga-float-header ion-no-border" :class="{ 'page-header--scrolled': headerScrolled }">
      <ion-toolbar class="page-header-toolbar">
        <div class="liga-float-bar">
          <button type="button" class="liga-float-circle menu-btn" aria-label="К списку релизов" @click="goBack">
            <ion-icon :icon="arrowBackOutline" />
          </button>
          <div class="liga-float-title-pill">
            <span class="liga-float-title-text">{{ pageTitle }}</span>
          </div>
          <div class="liga-float-spacer" aria-hidden="true" />
        </div>
      </ion-toolbar>
    </ion-header>

    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <p v-if="loading" class="empty-hint ion-padding">Загрузка…</p>
      <p v-else-if="loadError" class="empty-hint ion-padding">{{ loadError }}</p>

      <template v-else-if="selectedRelease && selectedProgram">
        <ion-card class="admin-card">
          <ion-card-header>
            <ion-card-title>Названия</ion-card-title>
            <p class="rename-hint">Короткое имя программы ({{ selectedProgram.shortName }}) не меняется — от него зависят папки на диске.</p>
          </ion-card-header>
          <ion-card-content>
            <ion-item>
              <ion-input
                label="Название программы"
                label-placement="stacked"
                v-model="editProgramName"
                placeholder="HypeDance"
              />
            </ion-item>
            <p class="rename-hint">Изменение названия программы видно у всех её релизов.</p>
            <ion-button expand="block" @click="saveProgramName" :disabled="savingProgramName || !canSaveProgramName">
              {{ savingProgramName ? 'Сохранение…' : 'Сохранить название программы' }}
            </ion-button>
            <ion-item class="ion-margin-top">
              <ion-input
                label="Номер релиза"
                label-placement="stacked"
                v-model="editReleaseName"
                placeholder="1 или 10"
              />
            </ion-item>
            <p class="rename-hint">При смене номера папка на сервере переименуется (например HD_01 → HD_10).</p>
            <ion-button expand="block" @click="saveReleaseName" :disabled="savingReleaseName || !canSaveReleaseName">
              {{ savingReleaseName ? 'Сохранение…' : 'Сохранить номер релиза' }}
            </ion-button>
          </ion-card-content>
        </ion-card>

        <ion-card class="admin-card">
          <ion-card-header>
            <ion-card-title>{{ selectedProgram.programName }} {{ selectedRelease.releaseName }}</ion-card-title>
          </ion-card-header>
          <ion-card-content class="release-catalog-settings">
            <ion-item lines="full" class="transparent-item">
              <ion-toggle v-model="editReleaseVisible">Видимый в каталоге</ion-toggle>
            </ion-item>
            <ion-item lines="full" class="transparent-item">
              <ion-input
                v-model.number="editReleasePriceRub"
                label="Цена, ₽ (0 — не продаётся отдельно; при тарифах ниже — запасной вариант)"
                label-placement="floating"
                type="number"
                inputmode="decimal"
              />
            </ion-item>
            <div class="release-tiers-section">
              <h3>Тарифы покупки</h3>
              <p class="release-tiers-hint">Если заданы тарифы, используется минимальная цена из доступных пользователю. Роль ограничивает видимость тарифа.</p>
              <div v-for="(t, i) in editReleasePriceTiers" :key="i" class="tier-row">
                <ion-input v-model="t.name" label="Название" label-placement="stacked" placeholder="Стандарт" />
                <ion-input v-model.number="t.amountRub" label="₽" label-placement="stacked" type="number" min="0" />
                <ion-select v-model="t.requiredAuthorityName" interface="popover" placeholder="Роль" cancel-text="Отмена">
                  <ion-select-option :value="null">—</ion-select-option>
                  <ion-select-option v-for="a in authorities" :key="a.name" :value="a.name">
                    {{ a.description || a.name }}
                  </ion-select-option>
                </ion-select>
                <ion-button fill="clear" size="small" @click="removeReleaseTier(i)">×</ion-button>
              </div>
              <ion-button fill="outline" size="small" @click="addReleaseTier">+ Тариф</ion-button>
            </div>
            <ion-button expand="block" @click="saveReleaseCatalogSettings" :disabled="savingReleaseCatalog">
              {{ savingReleaseCatalog ? 'Сохранение…' : 'Сохранить цену и видимость' }}
            </ion-button>
          </ion-card-content>
          <ion-card-content>
            <ion-card v-if="uploadQueue.length > 0" color="tertiary" class="upload-queue-card">
              <ion-card-header>
                <ion-card-title>Имена на сервере</ion-card-title>
                <p class="upload-queue-hint">
                  При необходимости измените имя каждого файла (включая ведущие нули), затем нажмите «Загрузить».
                  <template v-if="uploadQueueType === 'music' || uploadQueueType === 'video'">
                    Расширение совпадает с исходным файлом и не меняется.
                  </template>
                </p>
              </ion-card-header>
              <ion-card-content>
                <ion-list lines="full" class="transparent-list">
                  <ion-item v-for="(q, qIdx) in uploadQueue" :key="'q-' + qIdx">
                    <ion-label class="ion-text-wrap">
                      <p class="upload-queue-original">{{ q.file.name }}</p>
                      <ion-input
                        v-model="q.targetName"
                        label="Сохранить как"
                        label-placement="stacked"
                        :placeholder="q.file.name"
                        @ionInput="onReleaseUploadTargetNameInput(q, $event)"
                      ></ion-input>
                    </ion-label>
                  </ion-item>
                </ion-list>
                <ion-button expand="block" @click="flushUploadQueue()" :disabled="uploadQueueFlushing">
                  {{ uploadQueueFlushing ? 'Загрузка…' : 'Загрузить' }}
                </ion-button>
                <ion-button expand="block" fill="clear" color="medium" @click="clearUploadQueue()" :disabled="uploadQueueFlushing">
                  Отмена
                </ion-button>
              </ion-card-content>
            </ion-card>

            <ion-item>
              <ion-label>
                <h2>Музыка</h2>
                <p>Аудио файлы (конвертируются в MP3)</p>
              </ion-label>
            </ion-item>
            <ion-button expand="block" @click="selectFiles('music')">
              <ion-icon slot="start" :icon="musicalNotesOutline"></ion-icon>
              Загрузить музыку
            </ion-button>
            <input
              type="file"
              ref="musicInput"
              multiple
              :accept="adminAudioFileAccept"
              style="display: none"
              @change="uploadFiles('music', $event)"
            />
            <div class="upload-modal-file-list">
              <ion-list v-if="releaseMediaFiles.music.length" class="transparent-list">
                <ion-item v-for="f in releaseMediaFiles.music" :key="f.id" lines="full">
                  <ion-label>
                    <h3>{{ f.name }}</h3>
                    <p>{{ formatFileSize(f.weight) }}</p>
                  </ion-label>
                  <ion-button slot="end" fill="clear" @click="promptRenameFile('music', f)">
                    <ion-icon :icon="createOutline"></ion-icon>
                  </ion-button>
                  <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('music', f)">
                    <ion-icon :icon="trashOutline"></ion-icon>
                  </ion-button>
                </ion-item>
              </ion-list>
              <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных файлов</p>
            </div>

            <ion-item>
              <ion-label>
                <h2>Видео</h2>
                <p>Видео файлы (конвертируются для стриминга)</p>
              </ion-label>
            </ion-item>
            <ion-button expand="block" @click="selectFiles('video')">
              <ion-icon slot="start" :icon="videocamOutline"></ion-icon>
              Загрузить видео
            </ion-button>
            <input type="file" ref="videoInput" multiple accept="video/*" style="display: none" @change="uploadFiles('video', $event)" />
            <div class="upload-modal-file-list">
              <ion-list v-if="releaseMediaFiles.video.length" class="transparent-list">
                <ion-item v-for="f in releaseMediaFiles.video" :key="f.id" lines="full">
                  <ion-label>
                    <h3>{{ f.name }}</h3>
                    <p>{{ formatFileSize(f.weight) }}</p>
                  </ion-label>
                  <ion-button slot="end" fill="clear" @click="promptRenameFile('video', f)">
                    <ion-icon :icon="createOutline"></ion-icon>
                  </ion-button>
                  <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('video', f)">
                    <ion-icon :icon="trashOutline"></ion-icon>
                  </ion-button>
                </ion-item>
              </ion-list>
              <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных файлов</p>
            </div>

            <ion-item>
              <ion-label>
                <h2>Гайд (PDF)</h2>
                <p>PDF файл (конвертируется в изображения)</p>
              </ion-label>
            </ion-item>
            <ion-button expand="block" @click="selectFiles('guide')">
              <ion-icon slot="start" :icon="documentTextOutline"></ion-icon>
              Загрузить гайд
            </ion-button>
            <input type="file" ref="guideInput" accept=".pdf,application/pdf" style="display: none" @change="uploadFiles('guide', $event)" />
            <div class="upload-modal-file-list">
              <ion-list v-if="releaseMediaFiles.guide.length" class="transparent-list">
                <ion-item v-for="f in releaseMediaFiles.guide" :key="f.id" lines="full">
                  <ion-label>
                    <h3>{{ f.name }}</h3>
                    <p>{{ formatFileSize(f.weight) }}</p>
                  </ion-label>
                  <ion-button slot="end" fill="clear" @click="promptRenameFile('guide', f)">
                    <ion-icon :icon="createOutline"></ion-icon>
                  </ion-button>
                  <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('guide', f)">
                    <ion-icon :icon="trashOutline"></ion-icon>
                  </ion-button>
                </ion-item>
              </ion-list>
              <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных PDF</p>
            </div>

            <ion-item>
              <ion-label>
                <h2>Обложка</h2>
                <p>Изображение обложки релиза</p>
              </ion-label>
            </ion-item>
            <ion-button expand="block" @click="selectFiles('cover')">
              <ion-icon slot="start" :icon="imageOutline"></ion-icon>
              Загрузить обложку
            </ion-button>
            <input type="file" ref="coverInput" accept="image/*" style="display: none" @change="uploadFiles('cover', $event)" />
            <div class="upload-modal-file-list">
              <ion-list v-if="releaseMediaFiles.cover.length" class="transparent-list">
                <ion-item v-for="f in releaseMediaFiles.cover" :key="f.id" lines="full">
                  <ion-label>
                    <h3>{{ f.name }}</h3>
                    <p>{{ formatFileSize(f.weight) }}</p>
                  </ion-label>
                  <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('cover', f)">
                    <ion-icon :icon="trashOutline"></ion-icon>
                  </ion-button>
                </ion-item>
              </ion-list>
              <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет обложки</p>
            </div>

            <div v-if="uploadProgress.length > 0" class="upload-progress-list">
              <h3>Загрузка файлов</h3>
              <div v-for="(progress, idx) in uploadProgress" :key="'progress-' + idx" class="upload-progress-item">
                <p>{{ progress.fileName }}</p>
                <ion-progress-bar :value="progress.percent / 100"></ion-progress-bar>
                <p class="progress-text">{{ progress.status }}</p>
              </div>
            </div>
            <ion-button expand="block" fill="outline" color="danger" class="ion-margin-top" @click="confirmDeleteRelease()">
              <ion-icon slot="start" :icon="trashOutline"></ion-icon>
              Удалить релиз
            </ion-button>
          </ion-card-content>
        </ion-card>
      </template>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from '@/model/globalcontext.model';
import { Program } from '@/model/program.model';
import { ProgramRelease } from '@/model/programrelease.model';
import { Authority } from '@/model/authority.model';
import AdminService from '@/services/admin.service';
import releasesService from '@/services/releases.service';
import { ADMIN_AUDIO_FILE_ACCEPT } from '@/utils/admin-audio-upload-accept';
import { preserveFileExtension } from '@/utils/filename-extension';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonProgressBar,
  IonSelect,
  IonSelectOption,
  IonToggle,
  IonToolbar,
  alertController,
  toastController,
} from '@ionic/vue';
import {
  arrowBackOutline,
  createOutline,
  documentTextOutline,
  imageOutline,
  musicalNotesOutline,
  trashOutline,
  videocamOutline,
} from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import axios from 'axios';

export default defineComponent({
  name: 'AdminReleaseContent',
  components: {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonProgressBar,
    IonSelect,
    IonSelectOption,
    IonToggle,
    IonToolbar,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      isMobile: globalContext.isMobile,
      arrowBackOutline,
      documentTextOutline,
      imageOutline,
      musicalNotesOutline,
      trashOutline,
      videocamOutline,
      createOutline,
      adminAudioFileAccept: ADMIN_AUDIO_FILE_ACCEPT,
    };
  },
  data() {
    return {
      headerScrolled: false,
      loading: true,
      loadError: '' as string,
      selectedProgram: null as Program | null,
      selectedRelease: null as ProgramRelease | null,
      editProgramName: '',
      editReleaseName: '',
      savingProgramName: false,
      savingReleaseName: false,
      uploadProgress: [] as Array<{ fileName: string; percent: number; status: string }>,
      releaseMediaFiles: {
        music: [] as Array<{ id: string; name: string; weight: number }>,
        video: [] as Array<{ id: string; name: string; weight: number }>,
        guide: [] as Array<{ id: string; name: string; weight: number }>,
        cover: [] as Array<{ id: string; name: string; weight: number }>,
      },
      releaseMediaLoading: false,
      uploadQueue: [] as Array<{ file: File; targetName: string }>,
      uploadQueueType: null as 'music' | 'video' | 'guide' | 'cover' | null,
      uploadQueueFlushing: false,
      editReleaseVisible: false,
      editReleasePriceRub: 0 as number,
      editReleasePriceTiers: [] as { name: string; amountRub: number; requiredAuthorityName: string | null }[],
      authorities: [] as Authority[],
      adminService: new AdminService(),
      savingReleaseCatalog: false,
    };
  },
  computed: {
    pageTitle(): string {
      if (this.selectedProgram && this.selectedRelease) {
        return `${this.selectedProgram.programName || ''} ${this.selectedRelease.releaseName || ''}`.trim() || 'Контент релиза';
      }
      return 'Контент релиза';
    },
    canSaveProgramName(): boolean {
      const next = this.editProgramName.trim();
      return !!next && next !== (this.selectedProgram?.programName || '');
    },
    canSaveReleaseName(): boolean {
      const next = this.editReleaseName.trim();
      return !!next && next !== (this.selectedRelease?.releaseName || '');
    },
    releaseId(): number | null {
      const raw = this.$route.params.id;
      const n = Number(Array.isArray(raw) ? raw[0] : raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
  },
  watch: {
    '$route.params.id'() {
      void this.loadRelease();
    },
  },
  mounted() {
    void this.loadAuthorities();
    void this.loadRelease();
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'AdminReleases' });
    },
    onContentScroll(ev: CustomEvent) {
      this.headerScrolled = (ev.detail?.scrollTop ?? 0) > 0;
    },
    async loadAuthorities() {
      try {
        this.authorities = await this.adminService.getAuthoritiesFull();
      } catch {
        this.authorities = [];
      }
    },
    async loadRelease() {
      const id = this.releaseId;
      if (!id) {
        this.loading = false;
        this.loadError = 'Некорректный идентификатор релиза';
        return;
      }
      this.loading = true;
      this.loadError = '';
      try {
        const { data } = await axios.get(`/api/releases/${id}`);
        const program = data.program as Program | undefined;
        if (!program?.id) {
          this.loadError = 'У релиза нет связанной программы';
          this.selectedRelease = null;
          this.selectedProgram = null;
          return;
        }
        this.selectedRelease = data as ProgramRelease;
        this.selectedProgram = program;
        this.editProgramName = program.programName || '';
        this.editReleaseName = data.releaseName || '';
        this.editReleaseVisible = !!data.visible;
        const pk = data.priceKopecks;
        this.editReleasePriceRub = pk != null && pk > 0 ? Math.round(pk) / 100 : 0;
        this.uploadProgress = [];
        await this.loadReleasePriceTiers(id);
        await this.loadReleaseMediaLists();
      } catch (e) {
        console.error(e);
        this.loadError = 'Не удалось загрузить релиз';
        this.selectedRelease = null;
        this.selectedProgram = null;
      } finally {
        this.loading = false;
      }
    },
    async saveProgramName() {
      if (!this.selectedProgram?.id || !this.canSaveProgramName) return;
      this.savingProgramName = true;
      try {
        await axios.patch(
          `/api/programs/${this.selectedProgram.id}`,
          { id: this.selectedProgram.id, programName: this.editProgramName.trim() },
          { headers: { 'Content-Type': 'application/merge-patch+json' } }
        );
        this.selectedProgram.programName = this.editProgramName.trim();
        const toast = await toastController.create({
          message: 'Название программы сохранено',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error(e);
        const toast = await toastController.create({
          message: 'Не удалось сохранить название программы',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.savingProgramName = false;
      }
    },
    async saveReleaseName() {
      if (!this.selectedRelease?.id || !this.canSaveReleaseName) return;
      this.savingReleaseName = true;
      try {
        const { data } = await axios.put(`/api/admin/releases/${this.selectedRelease.id}/rename`, {
          releaseName: this.editReleaseName.trim(),
        });
        this.selectedRelease.releaseName = data.releaseName;
        this.editReleaseName = data.releaseName || '';
        const toast = await toastController.create({
          message: 'Номер релиза сохранён',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        await this.loadReleaseMediaLists();
      } catch (e: unknown) {
        console.error(e);
        const err = e as { response?: { data?: { detail?: string; title?: string } } };
        const detail = err.response?.data?.detail || err.response?.data?.title || 'Не удалось переименовать релиз';
        const toast = await toastController.create({
          message: detail,
          duration: 4000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.savingReleaseName = false;
      }
    },
    async loadReleasePriceTiers(releaseId?: number) {
      if (!releaseId) {
        this.editReleasePriceTiers = [];
        return;
      }
      try {
        const tiers = await releasesService.getPriceTiers(releaseId);
        this.editReleasePriceTiers = tiers.map((t) => ({
          name: t.name,
          amountRub: t.amountKopecks > 0 ? t.amountKopecks / 100 : 0,
          requiredAuthorityName: t.requiredAuthorityName ?? null,
        }));
      } catch {
        this.editReleasePriceTiers = [];
      }
    },
    addReleaseTier() {
      this.editReleasePriceTiers.push({ name: '', amountRub: 0, requiredAuthorityName: null });
    },
    removeReleaseTier(index: number) {
      this.editReleasePriceTiers.splice(index, 1);
    },
    async saveReleaseCatalogSettings() {
      const rel = this.selectedRelease;
      if (!rel?.id) return;
      this.savingReleaseCatalog = true;
      try {
        const rub = Number(this.editReleasePriceRub);
        const priceKopecks = !Number.isFinite(rub) || rub <= 0 ? 0 : Math.round(rub * 100);
        await axios.patch(
          `/api/releases/${rel.id}`,
          { id: rel.id, visible: this.editReleaseVisible, priceKopecks },
          { headers: { 'Content-Type': 'application/merge-patch+json' } }
        );
        const tiersPayload = this.editReleasePriceTiers
          .filter((t) => t.name.trim())
          .map((t) => ({
            name: t.name.trim(),
            amountKopecks: Math.max(0, Math.round(Number(t.amountRub) * 100)),
            requiredAuthorityName: t.requiredAuthorityName || null,
          }));
        await releasesService.setPriceTiers(rel.id, tiersPayload);
        rel.visible = this.editReleaseVisible;
        rel.priceKopecks = priceKopecks;
        const toast = await toastController.create({
          message: 'Сохранено',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error(e);
        const toast = await toastController.create({
          message: 'Не удалось сохранить',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.savingReleaseCatalog = false;
      }
    },
    clearUploadQueue() {
      this.uploadQueue = [];
      this.uploadQueueType = null;
      this.uploadQueueFlushing = false;
    },
    onReleaseUploadTargetNameInput(q: { file: File; targetName: string }, ev: CustomEvent) {
      const raw = (ev.detail as { value?: string }).value ?? '';
      const fixed = preserveFileExtension(q.file.name, raw);
      if (fixed !== q.targetName) {
        q.targetName = fixed;
      }
    },
    async flushUploadQueue() {
      if (!this.uploadQueueType || !this.selectedRelease || this.uploadQueue.length === 0) return;
      this.uploadQueueFlushing = true;
      const type = this.uploadQueueType;
      const items = [...this.uploadQueue];
      this.uploadQueue = [];
      this.uploadQueueType = null;
      try {
        for (const item of items) {
          const raw = item.targetName?.trim() ? item.targetName.trim() : item.file.name;
          const name =
            type === 'music' || type === 'video' ? preserveFileExtension(item.file.name, raw) : raw;
          await this.uploadSingleFile(type, item.file, name);
        }
      } finally {
        this.uploadQueueFlushing = false;
      }
    },
    async loadReleaseMediaLists() {
      if (!this.selectedRelease) return;
      this.releaseMediaLoading = true;
      const id = this.selectedRelease.id;
      try {
        const [music, video, guide, cover] = await Promise.all([
          axios.get(`/api/releases/${id}/media/music`).then((r) => r.data),
          axios.get(`/api/releases/${id}/media/video`).then((r) => r.data),
          axios.get(`/api/releases/${id}/media/guide`).then((r) => r.data),
          axios.get(`/api/releases/${id}/media/cover`).then((r) => r.data),
        ]);
        this.releaseMediaFiles = { music, video, guide, cover };
      } catch (error) {
        console.error('Failed to load release media lists', error);
        const toast = await toastController.create({
          message: 'Не удалось загрузить списки файлов',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.releaseMediaLoading = false;
      }
    },
    formatFileSize(bytes: number): string {
      if (bytes < 1024) return `${bytes} Б`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
    },
    async confirmDeleteFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      const typeLabel =
        subdir === 'music' ? 'музыку' : subdir === 'video' ? 'видео' : subdir === 'guide' ? 'PDF' : 'обложку';
      const alert = await alertController.create({
        header: 'Удалить файл?',
        message: `Удалить ${typeLabel}: ${f.name}?${
          subdir === 'guide' ? ' Будут также удалены все сконвертированные страницы (изображения).' : ''
        }`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.deleteFile(subdir, f) },
        ],
      });
      await alert.present();
    },
    async promptRenameFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      const selected = this.selectedRelease;
      if (!selected) return;
      const releaseId = selected.id;
      const lockExt = subdir === 'music' || subdir === 'video';
      const alert = await alertController.create({
        header: 'Переименовать файл',
        message: lockExt
          ? `Текущее имя: ${f.name}. Расширение файла остаётся прежним.`
          : `Текущее имя: ${f.name}`,
        inputs: [
          {
            name: 'newName',
            type: 'text',
            value: f.name,
            placeholder: lockExt ? 'Новое имя без смены расширения' : 'Новое имя с расширением',
          },
        ],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data) => {
              const payload = data as { newName?: string };
              const raw = payload?.newName?.trim();
              if (!raw) return true;
              const to = lockExt ? preserveFileExtension(f.id, raw) : raw;
              if (to === f.id) return true;
              try {
                await axios.put(`/api/admin/releases/${releaseId}/file/rename`, null, {
                  params: { subdir, from: f.id, to },
                });
                const toast = await toastController.create({
                  message: `Файл переименован в «${to}»`,
                  duration: 2000,
                  color: 'success',
                });
                await toast.present();
                await this.loadReleaseMediaLists();
              } catch (error) {
                console.error('Rename failed', error);
                const toast = await toastController.create({
                  message: 'Не удалось переименовать',
                  duration: 3000,
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },
    async deleteFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      if (!this.selectedRelease) return;
      try {
        await axios.delete(`/api/admin/releases/${this.selectedRelease.id}/file`, {
          params: { subdir, filename: f.id },
        });
        const toast = await toastController.create({
          message: `${f.name} удалён`,
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        await this.loadReleaseMediaLists();
      } catch (error) {
        console.error('Delete file failed', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления файла',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },
    async confirmDeleteRelease() {
      if (!this.selectedRelease || !this.selectedProgram) return;
      const alert = await alertController.create({
        header: 'Удалить релиз?',
        message: `Релиз «${this.selectedProgram.programName} ${this.selectedRelease.releaseName}» будет удалён. Это действие нельзя отменить.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.deleteRelease() },
        ],
      });
      await alert.present();
    },
    async deleteRelease() {
      if (!this.selectedRelease?.id) return;
      try {
        await axios.delete(`/api/releases/${this.selectedRelease.id}`);
        const toast = await toastController.create({
          message: 'Релиз удалён',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        this.goBack();
      } catch (error) {
        console.error('Failed to delete release:', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления релиза',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },
    selectFiles(type: 'music' | 'video' | 'guide' | 'cover') {
      const refName = (
        {
          music: 'musicInput',
          video: 'videoInput',
          guide: 'guideInput',
          cover: 'coverInput',
        } as const
      )[type];
      const r = this.$refs[refName] as HTMLInputElement | HTMLInputElement[] | undefined;
      const input = Array.isArray(r) ? r[0] : r;
      input?.click();
    },
    async uploadFiles(type: 'music' | 'video' | 'guide' | 'cover', event: Event) {
      if (!this.selectedRelease) return;
      const input = event.target as HTMLInputElement;
      const files = input.files;
      if (!files || files.length === 0) return;
      this.uploadQueueType = type;
      this.uploadQueue = Array.from(files).map((file) => ({
        file,
        targetName: file.name,
      }));
      input.value = '';
    },
    async uploadSingleFile(type: string, file: File, serverFileName?: string) {
      if (!this.selectedRelease) return;
      const displayName = serverFileName && serverFileName.trim() ? serverFileName.trim() : file.name;
      this.uploadProgress.push({
        fileName: displayName,
        percent: 0,
        status: 'Загрузка...',
      });
      const progressIndex = this.uploadProgress.length - 1;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', displayName);
      const url = `/api/admin/releases/${this.selectedRelease.id}/upload/${type}`;
      const token =
        localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
      try {
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (ev: ProgressEvent) => {
            if (ev.lengthComputable && ev.total > 0) {
              const percent = Math.round((100 * ev.loaded) / ev.total);
              const item = this.uploadProgress[progressIndex];
              if (item) {
                item.percent = percent;
                item.status = percent >= 100 ? 'Загружено' : 'Загрузка...';
              }
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else {
              const e = new Error(`Upload failed: ${xhr.status}`) as Error & { status?: number };
              e.status = xhr.status;
              reject(e);
            }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(formData);
        });
        const item = this.uploadProgress[progressIndex];
        if (item) {
          item.percent = 100;
          item.status = 'Загружено';
        }
        await this.loadReleaseMediaLists();
        const toast = await toastController.create({
          message: `${displayName} загружен`,
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (error) {
        console.error('Upload failed:', error);
        const item = this.uploadProgress[progressIndex];
        if (item) item.status = 'Ошибка';
        const err = error as Error & { status?: number; response?: { status?: number } };
        const status = err.status ?? err.response?.status;
        const toastMessage =
          status === 413
            ? `Файл слишком большой (413). Увеличьте client_max_body_size в nginx и лимиты multipart/undertow в Spring (сейчас до 5 ГБ на сервере).`
            : `Ошибка загрузки ${displayName}`;
        const toast = await toastController.create({
          message: toastMessage,
          duration: status === 413 ? 5000 : 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  background: var(--liga-surface, rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(10px);
  --color: var(--liga-chrome-text);
}

.admin-card ion-card-title {
  color: var(--liga-chrome-text);
  font-weight: 600;
}

.rename-hint {
  margin: 6px 0 10px;
  font-size: 0.85rem;
  color: var(--liga-chrome-text-muted);
}

.empty-hint {
  color: var(--liga-chrome-text-muted);
  font-style: italic;
  padding: 8px 0;
}

.admin-card ion-list {
  background: transparent;
}

.admin-card ion-item {
  --background: transparent;
  --background-activated: transparent;
  --background-hover: transparent;
  --background-focused: transparent;
  --color: var(--liga-chrome-text);
  --border-color: var(--liga-surface-border);
}

.admin-card ion-item::part(native) {
  background: transparent;
}

.admin-card ion-item ion-label h2,
.admin-card ion-item ion-label h3 {
  color: var(--liga-chrome-text);
  font-weight: 500;
}

.admin-card ion-item ion-label p {
  color: var(--liga-chrome-text-muted);
  font-size: 0.9em;
}

.admin-card ion-button:not([fill='clear']) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: var(--liga-on-accent, #fff);
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill='clear'] {
  --color: var(--ion-color-orange);
  font-weight: 600;
}

.upload-progress-list {
  margin-top: 20px;
}

.upload-progress-item {
  margin-bottom: 12px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.upload-progress-item p {
  margin: 4px 0;
  font-size: 0.9rem;
  color: var(--liga-chrome-text);
}

.progress-text {
  color: var(--liga-chrome-text-muted);
  font-size: 0.8rem;
}

.release-tiers-section {
  margin: 12px 0 16px;
}
.release-tiers-section h3 {
  margin: 0 0 6px;
  font-size: 16px;
  color: var(--liga-chrome-text);
}
.release-tiers-hint {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--liga-chrome-text-muted);
}
.tier-row {
  display: grid;
  grid-template-columns: 1fr 90px 1fr auto;
  gap: 8px;
  align-items: end;
  margin-bottom: 8px;
}
@media (max-width: 720px) {
  .tier-row {
    grid-template-columns: 1fr;
  }
}
.upload-progress-item ion-progress-bar {
  --progress-background: var(--ion-color-orange);
  --background: rgba(255, 255, 255, 0.1);
}

.upload-modal-file-list {
  margin: 8px 0 16px;
}

.upload-queue-hint,
.upload-queue-original {
  color: var(--liga-chrome-text-muted);
  font-size: 0.85rem;
}
</style>
